/* eslint-disable no-trailing-spaces */
class PromoBar extends HTMLElement {
  constructor () {
    super()
    if (Shopify.designMode) {
      this.classList.add('block')
      this.classList.remove('hidden')
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target === this.closest('.shopify-section')) {
          this.classList.add('block')
          this.classList.remove('hidden')
        }
      })
    }
    document.addEventListener('DOMContentLoaded', () => {
      this.cacheElements()
      this.init()
      this.initCountdown()
      this.setupVisibilityHandling()
      this.setupGlobalTriggers()
    })
  }

  cacheElements () {
    this.modalTitle = this.dataset.modalTitle
    this.modalContent = this.dataset.modalContent
    this.modalLinkText = this.dataset.modalLinkText
    this.modalLinkUrl = this.dataset.modalLinkUrl
    this.modalType = this.dataset.modalType
    this.modalImageDesktopUrl = this.dataset.modalImageDesktopUrl
    this.modalImageMobileUrl = this.dataset.modalImageMobileUrl
    this.modalImageDesktopAlt = this.dataset.modalImageDesktopAlt
    this.modalImageMobileAlt = this.dataset.modalImageMobileAlt
    this.contentType = this.dataset.contentType
    this.behaviour = this.dataset.behaviour
    this.period = parseInt(this.dataset.period, 10)
    this.storageKey = 'promoBar-lastClosed'
    this.countdownEndTime = this.dataset.countdownEnd
    this.daysLabel = this.dataset.countdownDaysLabel || 'days'
    this.dayLabel = this.dataset.countdownDayLabel || 'day'
    this.hoursLabel = this.dataset.countdownHoursLabel || 'hours'
    this.hourLabel = this.dataset.countdownHourLabel || 'hour'
    this.klaviyoId = this.dataset.klaviyoId
  }

  init () {
    if (this.shouldShowPromoBar()) {
      this.classList.remove('hidden')
      this.classList.add('block')
      
      // Emit event for sticky collision detection
      this.dispatchVisibilityEvent(true)
      
      // Preload images if modal type is image
      if (this.contentType === 'modal' && this.modalType === 'image') {
        this.preloadImages()
      }
    }

    if (this.contentType === 'modal' && this.reusableModal) {
      this.addEventListener('click', (event) => {
        const closeButton = event.target.closest('.promo-bar__close')
        if (closeButton) {
          this.handleClose()
          return
        }
        this.handleModalOpen()
      })
    } else if (this.contentType === 'klaviyo') {
      this.addEventListener('click', (event) => {
        const closeButton = event.target.closest('.promo-bar__close')
        if (closeButton) {
          this.handleClose()
          return
        }
        this.handleKlaviyoOpen()
      })
    } else {
      const closeButton = this.querySelector('.promo-bar__close')
      if (closeButton) {
        closeButton.addEventListener('click', () => this.handleClose())
      }
    }
  }

  initCountdown () {
    if (this.countdownEndTime) {
      this.updateCountdown()
      this.startCountdown()
    }
  }

  startCountdown () {
    if (!this.countdownEndTime) return
    
    this.stopCountdown()
    this.countdownInterval = setInterval(() => {
      this.updateCountdown()
    }, 1000)
  }

  stopCountdown () {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  updateCountdown () {
    const countdownElements = this.querySelectorAll('.promo-bar__countdown')
    if (!countdownElements.length || !this.countdownEndTime) return

    const now = new Date().getTime()
    const endTime = new Date(this.countdownEndTime).getTime()
    const timeLeft = endTime - now

    if (timeLeft <= 0) {
      countdownElements.forEach(element => {
        element.textContent = 'Expired'
      })
      this.stopCountdown()
      return
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    let displayText = ''
    if (days > 0) {
      const dayLabel = days === 1 ? this.dayLabel : this.daysLabel
      displayText = `${days} ${dayLabel}`
    } else {
      const hourLabel = hours === 1 ? this.hourLabel : this.hoursLabel
      displayText = `${hours} ${hourLabel}`
    }

    countdownElements.forEach(element => {
      element.textContent = displayText
    })
  }

  shouldShowPromoBar () {
    if (this.behaviour !== 'after_period') return true

    const lastClosed = localStorage.getItem(this.storageKey)
    if (!lastClosed) return true

    const lastClosedDate = new Date(parseInt(lastClosed, 10))
    const now = new Date()
    const daysSinceLastClosed = (now - lastClosedDate) / (1000 * 60 * 60 * 24)

    return daysSinceLastClosed >= this.period
  }

  handleClose () {
    if (this.behaviour === 'after_period') {
      localStorage.setItem(this.storageKey, Date.now().toString())
    }
    this.classList.add('hidden')
    this.stopCountdown()
    
    // Emit event for sticky collision detection
    this.dispatchVisibilityEvent(false)
  }

  handleKlaviyoOpen () {
    try {
      if (!this.klaviyoId) return
      window._klOnsite = window._klOnsite || []
      window._klOnsite.push(['openForm', this.klaviyoId])
    } catch (error) {
      console.error('Error opening Klaviyo form:', error)
    }
  }

  dispatchVisibilityEvent (isVisible) {
    // Dispatch custom event for other components to listen to
    const event = new CustomEvent('promobar:visibility', {
      detail: { 
        visible: isVisible,
        element: this,
      },
      bubbles: true,
    })
    document.dispatchEvent(event)
  }

  preloadImages () {
    this.imagesLoaded = false
    this.imageDimensions = {}
    const imagesToLoad = []

    // Determine which images to use - fallback to desktop image if mobile not provided
    const desktopImageUrl = this.modalImageDesktopUrl
    const mobileImageUrl = this.modalImageMobileUrl || this.modalImageDesktopUrl

    if (desktopImageUrl) {
      const img = new Image()
      img.src = desktopImageUrl
      imagesToLoad.push(new Promise((resolve, reject) => {
        img.onload = () => {
          this.imageDimensions.desktop = {
            width: img.naturalWidth,
            height: img.naturalHeight,
          }
          resolve()
        }
        img.onerror = reject
      }))
    }

    if (mobileImageUrl && mobileImageUrl !== desktopImageUrl) {
      const img = new Image()
      img.src = mobileImageUrl
      imagesToLoad.push(new Promise((resolve, reject) => {
        img.onload = () => {
          this.imageDimensions.mobile = {
            width: img.naturalWidth,
            height: img.naturalHeight,
          }
          resolve()
        }
        img.onerror = reject
      }))
    }

    if (imagesToLoad.length > 0) {
      Promise.all(imagesToLoad)
        .then(() => {
          this.imagesLoaded = true
        })
        .catch(() => {
          // Even if images fail to load, allow modal to open
          this.imagesLoaded = true
        })
    } else {
      this.imagesLoaded = true
    }
  }

  async handleModalOpen () {
    try {
      // For image modals, wait for images to load
      if (this.modalType === 'image' && !this.imagesLoaded) {
        // Show loading state
        this.classList.add('opacity-75', 'pointer-events-none')
        
        // Wait for images to load
        const checkImagesLoaded = () => {
          if (this.imagesLoaded) {
            this.classList.remove('opacity-75', 'pointer-events-none')
            this.openModal()
          } else {
            setTimeout(checkImagesLoaded, 100)
          }
        }
        checkImagesLoaded()
        return
      }
      
      this.openModal()
    } catch (error) {
      console.error('Error in handleModalOpen:', error)
    }
  }

  openModal () {
    // eslint-disable-next-line no-trailing-spaces
    this.prepareModalContent()

    const modalConfig = {
      width: 'min(600px, 100svw)',
    }

    // For image modals, use special configuration
    if (this.modalType === 'image') {
      modalConfig.padding = 'none'
      modalConfig.headerClassAdd = ['bg-transparent', 'border-0', 'mb-0', 'pb-0', 'h-[0px]']
      modalConfig.headerClassRemove = ['grid', 'border-b', 'mb-4', 'pb-2', 'gap-4', 'justify-between', 'items-center', 'w-full']
      modalConfig.closeClassAdd = ['absolute', 'top-4', 'right-4', 'z-30', 'shadow-lg']
      modalConfig.closeClassRemove = ['self-start']
      modalConfig.bodyClassRemove = ['overflow-y-auto', 'flex-grow']
      modalConfig.modalClassAdd = ['max-h-[90vh]', 'md:max-h-[700px]', 'md:rounded-lg', 'rounded-t-lg']
      modalConfig.modalClassRemove = ['rounded-lg']

      // For image modals, adjust modal width for better 2/3 aspect ratio display
      if (window.innerWidth >= 768) {
        // Desktop: Allow modal to size based on image, but constrain max height to 700px
        modalConfig.width = 'fit-content'
        modalConfig.modalClassAdd = [...(modalConfig.modalClassAdd || []), 'max-w-[90vw]']
        modalConfig.bodyClassAdd = ['relative', 'z-10', 'p-0', 'overflow-hidden', 'flex', 'items-center', 'justify-center']
      } else {
        // Mobile: Keep full width approach and no flex centering
        modalConfig.width = 'min(100vw, 100svw)'
        modalConfig.bodyClassAdd = ['relative', 'z-10', 'p-0', 'overflow-hidden']
      }
    } else {
      modalConfig.headerClassAdd = ['absolute', 'h-[80px]', 'top-0', 'left-0', 'right-0', 'z-20', 'block']
      modalConfig.headerClassRemove = ['border-b', 'mb-4', 'pb-2', 'grid']
      modalConfig.closeClassAdd = ['absolute', 'top-4', 'right-4', 'z-20']
      modalConfig.closeClassRemove = []
    }

    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: modalConfig,
      }),
    )

    const checkModalReady = () => {
      if (!this.modalBody) {
        requestAnimationFrame(checkModalReady)
        return
      }
      this.insertModalContent()
    }

    checkModalReady()
  }

  prepareModalContent () {
    try {
      this.modalContainer = document.createElement('div')

      if (this.modalType === 'image') {
        // For image modals, create a container with both images using Tailwind responsive classes
        this.modalContainer.classList.add('promo-modal-image', 'relative', 'w-full', 'h-full')

        // Determine which images to use - fallback to desktop image if mobile not provided
        const desktopImageUrl = this.modalImageDesktopUrl
        const mobileImageUrl = this.modalImageMobileUrl || this.modalImageDesktopUrl
        const desktopImageAlt = this.modalImageDesktopAlt || 'Modal image'
        const mobileImageAlt = this.modalImageMobileAlt || this.modalImageDesktopAlt || 'Modal image'

        // Create desktop image (hidden on mobile, shown on md+)
        // Desktop: height = 100%, width = auto, max height 700px
        if (desktopImageUrl) {
          const desktopImg = document.createElement('img')
          desktopImg.src = desktopImageUrl
          desktopImg.alt = desktopImageAlt
          desktopImg.classList.add('hidden', 'md:block', 'h-full', 'w-auto', 'max-h-[700px]', 'object-cover', 'rounded-lg', 'mx-auto')
          desktopImg.loading = 'eager'
          this.modalContainer.appendChild(desktopImg)
        }

        // Create mobile image (shown on mobile, hidden on md+)
        // Mobile: width = 100%, height = auto
        // Always create this element if we have any image (desktop or mobile)
        if (mobileImageUrl) {
          const mobileImg = document.createElement('img')
          mobileImg.src = mobileImageUrl
          mobileImg.alt = mobileImageAlt
          mobileImg.classList.add('block', 'md:hidden', 'w-full', 'h-auto', 'object-cover', 'md:rounded-lg', 'rounded-t-lg')
          mobileImg.loading = 'eager'
          this.modalContainer.appendChild(mobileImg)
        }

        // Add absolutely positioned link if URL exists - covers entire container
        if (this.modalLinkUrl) {
          const link = document.createElement('a')
          link.href = this.modalLinkUrl
          link.classList.add('absolute', 'inset-0', 'w-full', 'h-full', 'z-20', 'cursor-pointer')
          link.setAttribute('aria-label', this.modalLinkText || 'View more')
          
          // Add span with sr-only text to prevent a:empty CSS rule from hiding the link
          const linkText = document.createElement('span')
          linkText.classList.add('sr-only')
          linkText.textContent = this.modalLinkText || 'View more'
          link.appendChild(linkText)
          
          this.modalContainer.appendChild(link)
        }
      } else {
        // Handle text modals as before
        if (!this.modalContent) {
          throw new Error('No modal content available')
        }

        const tempDiv = document.createElement('div')
        let processedContent = this.modalContent
        if (this.modalContent.includes('%')) {
          try {
            processedContent = decodeURIComponent(this.modalContent)
          } catch (e) {
            // Content was not URL encoded, use as is
          }
        }
        tempDiv.innerHTML = processedContent

        this.modalContainer.classList.add('promo-modal', 'flex', 'flex-col', 'gap-4')

        const title = document.createElement('h4')
        title.classList.add('text-lg', 'font-body', 'font-semibold', 'normal-case', 'max-w-[90%]')
        title.textContent = this.modalTitle

        const content = document.createElement('div')
        content.classList.add('tb-in-page-content', 'tb-list-fix')
        content.innerHTML = tempDiv.innerHTML

        const link = this.modalLinkText && this.modalLinkUrl
          ? Object.assign(document.createElement('a'), {
            href: this.modalLinkUrl,
            textContent: this.modalLinkText,
            className: 'button button-primary',
          })
          : null

        this.modalContainer.append(...[title, content, link].filter(Boolean))
      }

      return this.modalContainer
    } catch (error) {
      console.error('Error in prepareModalContent:', error)
      throw error
    }
  }

  insertModalContent () {
    if (this.modalBody && this.modalContainer) {
      this.modalBody.innerHTML = ''
      this.modalBody.append(this.modalContainer.cloneNode(true))
    }
  }

  setupVisibilityHandling () {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopCountdown()
      } else {
        this.startCountdown()
      }
    })
  }

  setupGlobalTriggers () {
    if (!PromoBar.globalListenerAdded) {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('.promo-modal-trigger')
        if (trigger) {
          event.preventDefault()
          const promoBar = PromoBar.findModalPromoBar()
          if (promoBar) {
            if (promoBar.contentType === 'modal') {
              promoBar.handleModalOpen()
            } else if (promoBar.contentType === 'klaviyo') {
              promoBar.handleKlaviyoOpen()
            }
          }
        }
      })
      PromoBar.globalListenerAdded = true
    }
  }

  static findModalPromoBar () {
    const promoBars = document.querySelectorAll('promo-bar')
    for (const promoBar of promoBars) {
      if ((promoBar.contentType === 'modal' || promoBar.contentType === 'klaviyo') && 
          (promoBar.modalTitle || promoBar.modalContent || promoBar.klaviyoId)) {
        return promoBar
      }
    }
    return null
  }

  get reusableModal () {
    return document.querySelector('reusable-modal')
  }

  get modalBody () {
    return this.reusableModal?.querySelector('.reusable-modal__body')
  }
}

PromoBar.globalListenerAdded = false

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar)
}
